/* wbpk-export-def-class export default  */ class CurrencyHelper {

  /**
   * @typedef Currency
   * @property {number} minorUnit
   * @property {string} prefix
   */

  static WITHOUT_CURRENCY_CODE = false;
  static WITH_CURRENCY_CODE = true;
  static WITH_CURRENCY_CODE_IF_NON_UNIQUE_PREFIX = -1;

  /**
   * @type {Object.<string, Currency>}
   */
  static currencyDictionary = {};

  /**
   * @type {Map<string, boolean>}
   */
  static prefixNonUnqiuenessCache = new Map();

  /**
   * Set new currency dictionary
   * @param {Object.<string, Currency>} dictionary ISO currency dictionary.
   * @returns {void}
   */
  static setDictionary(dictionary) {
    CurrencyHelper.currencyDictionary = dictionary;
  }

  /**
   * Get currency data by currency code
   * @param {string} code Currency code
   * @returns {Currency}
   */
  static getCurrencyByCode(code) {
    return CurrencyHelper.currencyDictionary[code];
  }

  static getPrefixByCode(code) {
    return CurrencyHelper.currencyDictionary.hasOwnProperty(code)
      ? CurrencyHelper.currencyDictionary[code].prefix
      : '';
  }

  /**
   * Get currency minor unit by code
   * @returns {number}
   */
  static getSubunitByCode(code) {
    return CurrencyHelper.currencyDictionary[code] ? CurrencyHelper.currencyDictionary[code].minorUnit : 2;
  }

  /**
   * Returns rounded number in accordance with currency minor units
   * @param {number} value
   * @param {string} currency
   * @returns {number}
   */
  static roundMoney(value, currency) {
    const subunit = CurrencyHelper.getSubunitByCode(currency);
    return Math.round(value * 10 ** subunit) / 10 ** subunit;
  }

  /**
   * @param {number} value
   * @param {string} currency
   * @returns {number}
   */
  static moneyShiftLeft(value, currency) {
    return Math.round(value * 10 ** CurrencyHelper.getSubunitByCode(currency));
  }

  /**
   * @param {number} value
   * @param {string} currency
   * @returns {number}
   */
  static moneyShiftRight(value, currency) {
    return value / 10 ** CurrencyHelper.getSubunitByCode(currency);
  }

  /**
   * @param {number} value
   * @param {string} currency
   * @param {object} [options]
   * @param {string} [options.locale=en-US]
   * @param {boolean} [options.withSubunits=true]
   * @param {boolean} [options.withCurrencySign=true] - add currency sign as a prefix (example: $1,234.56)
   * @param {boolean|-1} [options.withCurrencyCode=-1] - add currency code as a prefix (example: USD 1,234.56 or USD $1,234.56 if the above option is also true), -1 means that the code would added if there is no unique sign for the currency
   * @param {boolean} [options.withCurrencyCodeAsPostfix=false] - add currency code as a postfix (example: 1,234.56 USD or $1,234.56 USD if the above option is also true)
   * @returns {string}
   */
  static moneyFormat(value, currency, options = {}) {
    const withSubunits = options.hasOwnProperty('withSubunits') ? options.withSubunits : true;
    let withCurrencySign = options.hasOwnProperty('withCurrencySign') ? options.withCurrencySign : true;
    let withCurrencyCode = options.hasOwnProperty('withCurrencyCode')
      ? options.withCurrencyCode
      : CurrencyHelper.WITH_CURRENCY_CODE_IF_NON_UNIQUE_PREFIX;
    const withCurrencyCodeAsPostfix = options.hasOwnProperty('withCurrencyCodeAsPostfix') ? options.withCurrencyCodeAsPostfix : false;

    const subunit = withSubunits ? CurrencyHelper.getSubunitByCode(currency) : 0;
    const moneyFormat = new Intl.NumberFormat(options.locale || 'en-US', {
      minimumFractionDigits: subunit,
      maximumFractionDigits: subunit,
    });
    let moneyPrefix = '';
    let moneyPostfix = '';

    const currencySign = CurrencyHelper.getCurrencyByCode(currency)?.prefix;

    if (withCurrencySign && !currencySign) {
      withCurrencyCode = CurrencyHelper.WITH_CURRENCY_CODE;
      withCurrencySign = false;
    }

    if (withCurrencyCode === CurrencyHelper.WITH_CURRENCY_CODE_IF_NON_UNIQUE_PREFIX) {
      if (withCurrencySign) {
        if (!CurrencyHelper.prefixNonUnqiuenessCache.has(currency)) {
          let isPrefixNonUnique = false;
          for (let otherCurrency in CurrencyHelper.currencyDictionary) {
            if (
              currency !== otherCurrency
              && CurrencyHelper.currencyDictionary.hasOwnProperty(otherCurrency)
              && CurrencyHelper.getCurrencyByCode(otherCurrency).prefix === currencySign
            ) {
              isPrefixNonUnique = true;
              break;
            }
          }
          CurrencyHelper.prefixNonUnqiuenessCache.set(currency, isPrefixNonUnique);
        }
        withCurrencyCode = CurrencyHelper.prefixNonUnqiuenessCache.get(currency)
          ? CurrencyHelper.WITH_CURRENCY_CODE
          : CurrencyHelper.WITHOUT_CURRENCY_CODE;
      } else {
        withCurrencyCode = CurrencyHelper.WITH_CURRENCY_CODE;
      }
    }

    if (withCurrencyCode === CurrencyHelper.WITH_CURRENCY_CODE && !withCurrencyCodeAsPostfix) {
      moneyPrefix += `${currency} `;
    }
    if (withCurrencyCode === CurrencyHelper.WITH_CURRENCY_CODE && withCurrencyCodeAsPostfix) {
      moneyPostfix += ` ${currency}`;
    }
    if (value < 0) {
      moneyPrefix += '-';
    }
    if (withCurrencySign) {
      moneyPrefix += currencySign;
    }
    return moneyPrefix + moneyFormat.format(Math.abs(value)) + moneyPostfix;
  }
}
